import { useState, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
    GET_TMS_ROUTE_WAYPOINTS_WITH_SORTING_CODE_BY_ROUTE_ID,
    GetTmsRouteWaypointsWithSortingCodeByRouteIdResponse,
    TmsRouteWaypointType,
} from '../route/graphql/queries/getTmsRoutes';

/**
 * TMS Waypoint 时区获取 Hook
 * 基于 GET_TMS_ROUTE_WAYPOINTS_WITH_SORTING_CODE_BY_ROUTE_ID 方式获取时区信息
 * 
 * @returns getRouteTimezones 函数，用于获取指定 route 的起点和终点时区
 * @returns getRouteWaypoints 函数，用于获取指定 route 的完整 waypoints 数组
 * @returns fetchRouteWaypoints 函数，用于手动触发查询指定 route 的 waypoints
 */
export const useTmsWaypointTimezones = () => {
  const [fetchRouteWaypointsQuery] = useLazyQuery<GetTmsRouteWaypointsWithSortingCodeByRouteIdResponse>(
    GET_TMS_ROUTE_WAYPOINTS_WITH_SORTING_CODE_BY_ROUTE_ID,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const [routeWaypointsData, setRouteWaypointsData] = useState<Map<number, TmsRouteWaypointType[]>>(new Map());

  const getRouteTimezones = useCallback(
    (routeId: number) => {
      const waypoints = routeWaypointsData.get(routeId);
      if (!waypoints || waypoints.length === 0) {
        return { originTimezone: undefined, destinationTimezone: undefined };
      }

      const sortedWaypoints = [...waypoints].sort((a, b) => a.waypointOrder - b.waypointOrder);
      const originWaypoint = sortedWaypoints.find((w) => w.waypointType === 1);
      const destinationWaypoint = sortedWaypoints.find((w) => w.waypointType === 3);

      return {
        originTimezone: originWaypoint?.waypointTimezone,
        destinationTimezone: destinationWaypoint?.waypointTimezone,
      };
    },
    [routeWaypointsData]
  );

  const getRouteWaypoints = useCallback(
    (routeId: number): TmsRouteWaypointType[] => {
      const waypoints = routeWaypointsData.get(routeId);
      return waypoints || [];
    },
    [routeWaypointsData]
  );

  const fetchRouteWaypoints = useCallback(
    async (routeId: number): Promise<TmsRouteWaypointType[]> => {
      try {
        const result = await fetchRouteWaypointsQuery({ variables: { routeId } });
        const waypoints =
            result.data?.getTmsRouteWaypointsWithSortingCodeByRouteId?.waypoints || [];
        
        if (waypoints.length > 0) {
          setRouteWaypointsData((prevData) => {
            const newData = new Map(prevData);
            newData.set(routeId, waypoints);
            return newData;
          });
        }
        
        return waypoints;
      } catch (error) {
        console.error('Failed to fetch route waypoints:', error);
        return [];
      }
    },
    [fetchRouteWaypointsQuery]
  );

  return {
    getRouteTimezones,
    getRouteWaypoints,
    fetchRouteWaypoints,
  };
};

